import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, CheckCircle, AlertCircle, Save } from 'lucide-react';

const ViewQuizSubmissionsModal = ({ quiz: initialQuiz, onClose }) => {
    const [submissions, setSubmissions] = useState([]);
    const [quizDetails, setQuizDetails] = useState(null); // Full quiz with questions
    const [loading, setLoading] = useState(true);
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [grading, setGrading] = useState(false);
    const [reviewing, setReviewing] = useState(false); // New state for reviewing answers
    const [score, setScore] = useState('');
    const [feedback, setFeedback] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const [submissionsRes, quizRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_BASE_URL}/quizzes/quiz-submissions/quiz/${initialQuiz.id}`, {
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                    }),
                    axios.get(`${process.env.REACT_APP_API_BASE_URL}/quizzes/${initialQuiz.id}`, {
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                    })
                ]);
                setSubmissions(submissionsRes.data);
                setQuizDetails(quizRes.data);
            } catch (error) {
                console.error('Error fetching data:', error);
                alert('Failed to load submissions or quiz details.');
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [initialQuiz.id]);

    const handleGrade = (submission) => {
        setSelectedSubmission(submission);
        setScore(submission.score);
        setFeedback(submission.feedback || '');
        setGrading(true);
        setReviewing(false);
    };

    const handleReview = (submission) => {
        setSelectedSubmission(submission);
        setReviewing(true);
        setGrading(false);
    };

    const handleSaveGrade = async () => {
        if (!score) return;

        try {
            setSaving(true);
            await axios.put(`${process.env.REACT_APP_API_BASE_URL}/quizzes/quiz-submissions/${selectedSubmission.id}/grade`, {
                score: parseFloat(score),
                feedback
            }, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            // Update local state
            setSubmissions(prev => prev.map(sub =>
                sub.id === selectedSubmission.id
                    ? { ...sub, score: parseFloat(score), feedback, status: 'graded', percentage: (parseFloat(score) / initialQuiz.totalPoints) * 100 }
                    : sub
            ));

            setGrading(false);
            setSelectedSubmission(null);
        } catch (error) {
            console.error('Error saving grade:', error);
            alert('Failed to save grade.');
        } finally {
            setSaving(false);
        }
    };

    const formatTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    const parseAnswers = (answersJson) => {
        try {
            return typeof answersJson === 'string' ? JSON.parse(answersJson) : answersJson;
        } catch (e) {
            return {};
        }
    };

    return (
        <div className="fixed inset-0 md:left-56 lg:left-64 bg-black/60 backdrop-blur-md flex items-center justify-center z-[100] p-0 md:p-4">
            <div className="bg-white w-full h-full md:max-h-full max-w-5xl rounded-2xl flex flex-col shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-300">

                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Quiz Submissions</h2>
                        <p className="text-sm text-gray-500">{initialQuiz.title ? initialQuiz.title.replace('AI Quiz:', 'Smart Quiz:') : ''}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X size={24} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : reviewing ? (
                        renderReviewView()
                    ) : (
                        renderSubmissionsList()
                    )}
                </div>

                {/* Grading Modal Overlay */}
                {grading && selectedSubmission && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg w-full max-w-md p-6 shadow-2xl">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Grade Submission</h3>
                            <p className="text-sm text-gray-600 mb-4">Student: {selectedSubmission.student?.name}</p>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Score (out of {initialQuiz.totalPoints})</label>
                                <input
                                    type="number"
                                    value={score}
                                    onChange={(e) => setScore(e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Feedback</label>
                                <textarea
                                    rows={3}
                                    value={feedback}
                                    onChange={(e) => setFeedback(e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => { setGrading(false); setSelectedSubmission(null); }}
                                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveGrade}
                                    disabled={saving}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                                >
                                    {saving ? 'Saving...' : 'Save Grade'} <Save size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    function renderSubmissionsList() {
        if (submissions.length === 0) {
            return (
                <div className="text-center py-12 text-gray-500">
                    No submissions found for this quiz.
                </div>
            );
        }

        return (
            <div className="space-y-4">
                {submissions.map((sub, idx) => (
                    <div key={sub.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div>
                            <h3 className="font-semibold text-gray-900">{sub.student?.name}</h3>
                            <p className="text-sm text-gray-500">{sub.student?.email}</p>
                            <div className="flex gap-4 mt-2 text-sm text-gray-600">
                                <span>Attempt: {sub.attemptNumber}</span>
                                <span>Submitted: {formatTime(sub.submittedAt)}</span>
                                <span>Time: {Math.floor(sub.timeSpent / 60)}m {sub.timeSpent % 60}s</span>
                            </div>
                        </div>

                        <div className="flex items-center gap-4 w-full md:w-auto">
                            <div className="text-right">
                                <div className="text-lg font-bold text-blue-600">
                                    {sub.score} / {initialQuiz.totalPoints}
                                </div>
                                <div className={`text-xs font-semibold px-2 py-0.5 rounded inline-block ${(sub.score / initialQuiz.totalPoints) >= (initialQuiz.passingScore / initialQuiz.totalPoints)
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                    }`}>
                                    {Number(sub.percentage || 0).toFixed(1)}%
                                </div>
                            </div>

                            <button
                                onClick={() => handleReview(sub)}
                                className="px-3 py-2 bg-blue-50 text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-100 text-sm font-medium"
                            >
                                Review Answers
                            </button>

                            <button
                                onClick={() => handleGrade(sub)}
                                className="px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm font-medium"
                            >
                                Grade
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    function renderReviewView() {
        if (!selectedSubmission || !quizDetails) return null;

        const studentAnswers = parseAnswers(selectedSubmission.answers);
        const questions = quizDetails.questions || [];

        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between mb-4">
                    <button
                        onClick={() => { setReviewing(false); setSelectedSubmission(null); }}
                        className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
                    >
                        <X size={20} /> Back to Submissions
                    </button>
                    <div className="text-right">
                        <h3 className="text-lg font-bold">{selectedSubmission.student?.name}</h3>
                        <p className="text-sm text-gray-500">Score: {selectedSubmission.score} / {initialQuiz.totalPoints}</p>
                    </div>
                </div>

                {questions.map((q, idx) => {
                    const studentAnswer = studentAnswers[q.id];
                    const isCorrect = studentAnswer?.toString().toLowerCase() === q.correctAnswer?.toString().toLowerCase();

                    return (
                        <div key={q.id} className={`border rounded-lg p-4 ${isCorrect ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                            <div className="flex justify-between items-start mb-2">
                                <h4 className="font-medium text-gray-900">Q{idx + 1}. {q.text}</h4>
                                <span className={`text-xs font-bold px-2 py-1 rounded ${isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                    {isCorrect ? 'Correct' : 'Incorrect'} ({isCorrect ? q.points : 0}/{q.points} pts)
                                </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 text-sm">
                                <div>
                                    <p className="font-semibold text-gray-700 mb-1">Student Answer:</p>
                                    <p className={`p-2 rounded ${isCorrect ? 'bg-green-100' : 'bg-white border border-red-200'}`}>
                                        {studentAnswer !== undefined ? studentAnswer : <span className="text-gray-400 italic">No answer</span>}
                                    </p>
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-700 mb-1">Correct Answer:</p>
                                    <p className="p-2 bg-gray-100 rounded border border-gray-200">
                                        {q.correctAnswer}
                                    </p>
                                </div>
                            </div>
                            {q.explanation && (
                                <div className="mt-3 text-sm text-gray-600 bg-white p-2 rounded border border-gray-100">
                                    <span className="font-semibold">Explanation:</span> {q.explanation}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }
};
// Helper for Review Mode variable (since I can't easily change the hook in the replacement)
// I will just use 'reviewing' state variable.


export default ViewQuizSubmissionsModal;
